import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaMoneyBillWave, FaSearch, FaPlus, FaSave, FaTimes, 
  FaEdit, FaTrash, FaCalendarAlt, FaFilter, FaSync,
  FaChevronLeft, FaChevronRight, FaCalendarDay, FaUser
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { NumericFormat } from 'react-number-format';
import { useToast } from '../components/ToastContext';

function GerenciarDepositos() {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();

  // Estados principais
  const [depositos, setDepositos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [modalEdicao, setModalEdicao] = useState(false);
  const [depositoEditando, setDepositoEditando] = useState(null);
  
  // Estados de filtro
  const [dataInicio, setDataInicio] = useState(() => {
    const hoje = new Date();
    return hoje.toISOString().split('T')[0];
  });
  const [dataFim, setDataFim] = useState(() => {
    const hoje = new Date();
    return hoje.toISOString().split('T')[0];
  });
  const [buscaDescricao, setBuscaDescricao] = useState('');
  const [filtroAplicado, setFiltroAplicado] = useState(false);
  
  // Estados de paginação
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [totalElementos, setTotalElementos] = useState(0);
  const [itensPorPagina] = useState(10);
  
  // Estado do formulário
  const [formDeposito, setFormDeposito] = useState({
    valor: '',
    descricao: ''
  });
  const [errors, setErrors] = useState({});

  // Headers com token
  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  // Formatação de moeda
  const formatarMoeda = (valor) => {
    if (valor === null || valor === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(valor);
  };

  // Converter string para número (moeda)
  const parseMoeda = (valorString) => {
    if (!valorString || typeof valorString !== 'string') return valorString || 0;

    // Previne que "10.5" (float como string) vire "105"
    if (valorString.includes('.') && !valorString.includes(',')) {
      return parseFloat(valorString) || 0;
    }

    const valorNumerico = parseFloat(
      valorString
        .replace(/\./g, '')
        .replace(',', '.')
        .replace(/[^\d,.-]/g, '')
    );
    return isNaN(valorNumerico) ? 0 : valorNumerico;
  };

  // Formatar input de moeda
  const formatarInputMoeda = (valor) => {
    if (!valor) return '';
    let apenasDigitos = valor.toString().replace(/\D/g, '');
    while (apenasDigitos.length < 3) {
      apenasDigitos = '0' + apenasDigitos;
    }
    const reais = apenasDigitos.slice(0, -2);
    const centavos = apenasDigitos.slice(-2);
    const reaisFormatados = reais.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${reaisFormatados},${centavos}`;
  };

  // Formatar data para exibição (JavaScript puro)
  const formatarData = (dataString) => {
    if (!dataString) return '';
    try {
      const data = new Date(dataString);
      const dia = String(data.getDate()).padStart(2, '0');
      const mes = String(data.getMonth() + 1).padStart(2, '0');
      const ano = data.getFullYear();
      const horas = String(data.getHours()).padStart(2, '0');
      const minutos = String(data.getMinutes()).padStart(2, '0');
      const segundos = String(data.getSeconds()).padStart(2, '0');
      return `${dia}/${mes}/${ano} ${horas}:${minutos}:${segundos}`;
    } catch {
      return dataString;
    }
  };

  // Buscar depositos com filtros
  const buscarDepositos = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/depositos/data/${dataInicio}/${dataFim}/pagina?page=${paginaAtual}&size=${itensPorPagina}`;
      
      const response = await axios.get(url, getAuthHeader());
      
      let dados = response.data.content;
      
      // Aplica filtro de descrição localmente
      if (buscaDescricao.trim()) {
        dados = dados.filter(deposito => 
          deposito.descricao && deposito.descricao.toLowerCase().includes(buscaDescricao.toLowerCase())
        );
      }
      
      setDepositos(dados);
      setTotalPaginas(response.data.totalPages);
      setTotalElementos(response.data.totalElements);
    } catch (error) {
      console.error('Erro ao buscar depositos:', error);
      addToast('error', 'Erro ao carregar depositos');
    } finally {
      setLoading(false);
    }
  };

  // Buscar depositos de hoje
  const buscarDepositosHoje = async () => {
    const hoje = new Date();
    const hojeFormatado = hoje.toISOString().split('T')[0];
    setDataInicio(hojeFormatado);
    setDataFim(hojeFormatado);
    setBuscaDescricao('');
    setPaginaAtual(0);
    setFiltroAplicado(true);
    
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/depositos/data/${hojeFormatado}/${hojeFormatado}/pagina?page=0&size=${itensPorPagina}`;
      const response = await axios.get(url, getAuthHeader());
      
      setDepositos(response.data.content);
      setTotalPaginas(response.data.totalPages);
      setTotalElementos(response.data.totalElements);
      setPaginaAtual(0);
      
      addToast('success', 'Exibindo depositos de hoje');
    } catch (error) {
      console.error('Erro ao buscar depositos de hoje:', error);
      addToast('error', 'Erro ao carregar depositos de hoje');
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    if (!dataInicio || !dataFim) {
      addToast('warning', 'Selecione as datas inicial e final');
      return;
    }
    if (new Date(dataInicio) > new Date(dataFim)) {
      addToast('warning', 'Data inicial não pode ser maior que data final');
      return;
    }
    setPaginaAtual(0);
    setFiltroAplicado(true);
    buscarDepositos();
  };

  // Limpar filtros
  const limparFiltros = () => {
    const hoje = new Date();
    setDataInicio(hoje.toISOString().split('T')[0]);
    setDataFim(hoje.toISOString().split('T')[0]);
    setBuscaDescricao('');
    setPaginaAtual(0);
    setFiltroAplicado(false);
    setTimeout(() => buscarDepositos(), 100);
  };

  // Carregar ao mudar página
  useEffect(() => {
    if (dataInicio && dataFim) {
      buscarDepositos();
    }
  }, [paginaAtual]);

  // Carregar inicial
  useEffect(() => {
    buscarDepositos();
  }, []);

  // Validar formulário
  const validarFormulario = () => {
    const newErrors = {};
    
    if (formDeposito.valor === undefined || formDeposito.valor === null || formDeposito.valor === '') {
      newErrors.valor = 'Valor é obrigatório';
    } else {
      const valorNum = typeof formDeposito.valor === 'string' ? parseMoeda(formDeposito.valor) : formDeposito.valor;
      if (valorNum <= 0) {
        newErrors.valor = 'Valor deve ser maior que zero';
      }
    }
    
    if (!formDeposito.descricao) {
      newErrors.descricao = 'Descrição é obrigatória';
    } else if (formDeposito.descricao.length < 3) {
      newErrors.descricao = 'Descrição deve ter pelo menos 3 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Salvar deposito (criar ou editar)
  const salvarDeposito = async () => {
    if (!validarFormulario()) return;
    
    setLoading(true);
    try {
      const valorNumerico = typeof formDeposito.valor === 'string' ? parseMoeda(formDeposito.valor) : (formDeposito.valor || 0);
      const dadosDeposito = {
        valor: valorNumerico,
        descricao: formDeposito.descricao,
        conta: { idConta: 1 }
      };
      
      if (modalEdicao && depositoEditando) {
        await axios.put(
          `${API_BASE_URL}/depositos/${depositoEditando.idDeposito}`,
          { ...dadosDeposito, idDeposito: depositoEditando.idDeposito },
          getAuthHeader()
        );
        addToast('success', 'Deposito atualizado com sucesso!');
      } else {
        await axios.post(
          `${API_BASE_URL}/depositos`,
          dadosDeposito,
          getAuthHeader()
        );
        addToast('success', 'Deposito adicionado com sucesso!');
      }
      
      fecharModal();
      buscarDepositos();
    } catch (error) {
      console.error('Erro ao salvar deposito:', error);
      addToast('error', `Erro ao salvar deposito: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal para novo deposito
  const abrirModalNovo = () => {
    setModalEdicao(false);
    setDepositoEditando(null);
    setFormDeposito({
      valor: '',
      descricao: ''
    });
    setErrors({});
    setModalAberto(true);
  };

  // Abrir modal para editar deposito
  const abrirModalEditar = (deposito) => {
    setModalEdicao(true);
    setDepositoEditando(deposito);
    setFormDeposito({
      valor: deposito.valor || 0,
      descricao: deposito.descricao || ''
    });
    setErrors({});
    setModalAberto(true);
  };

  // Fechar modal
  const fecharModal = () => {
    setModalAberto(false);
    setModalEdicao(false);
    setDepositoEditando(null);
    setFormDeposito({
      valor: '',
      descricao: ''
    });
    setErrors({});
  };

  // Excluir deposito
  const excluirDeposito = async (idDeposito) => {
    if (!window.confirm('Tem certeza que deseja excluir este deposito?')) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/depositos/${idDeposito}`, getAuthHeader());
      addToast('success', 'Deposito excluído com sucesso!');
      buscarDepositos();
    } catch (error) {
      console.error('Erro ao excluir deposito:', error);
      addToast('error', 'Erro ao excluir deposito');
    } finally {
      setLoading(false);
    }
  };

  // Mudar página
  const mudarPagina = (novaPagina) => {
    if (novaPagina >= 0 && novaPagina < totalPaginas) {
      setPaginaAtual(novaPagina);
    }
  };

  // Obter nome do usuário (assumindo que vem no objeto deposito)
  const getNomeUsuario = (deposito) => {
    if (deposito.usuarioQueCadastrouDTO && deposito.usuarioQueCadastrouDTO.nome) {
      return deposito.usuarioQueCadastrouDTO.nome;
    }
    return 'Usuário não informado';
  };

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <div style={styles.header}>
          <div style={styles.titleContainer}>
            <FaMoneyBillWave style={styles.titleIcon} />
            <h1 style={styles.title}>GERENCIAR DEPÓSITOS</h1>
          </div>
        </div>

        <div style={styles.formContainer}>
          {/* Filtros - Layout em linha única */}
          <div style={styles.filtrosContainer}>
            <div style={styles.filtrosWrapper}>
              <div style={styles.filtroGroup}>
                <label style={styles.filtroLabel}>
                  <FaCalendarAlt style={styles.filtroIcon} />
                  Data Início
                </label>
                <input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  style={styles.filtroInput}
                />
              </div>
              
              <div style={styles.filtroGroup}>
                <label style={styles.filtroLabel}>
                  <FaCalendarAlt style={styles.filtroIcon} />
                  Data Fim
                </label>
                <input
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  style={styles.filtroInput}
                />
              </div>
              
              <div style={styles.filtroGroupBusca}>
                <label style={styles.filtroLabel}>
                  <FaSearch style={styles.filtroIcon} />
                  Buscar descrição
                </label>
                <input
                  type="text"
                  value={buscaDescricao}
                  onChange={(e) => setBuscaDescricao(e.target.value)}
                  placeholder="Digite parte da descrição..."
                  style={styles.filtroInputBusca}
                  onKeyPress={(e) => e.key === 'Enter' && aplicarFiltros()}
                />
              </div>
              
              <div style={styles.filtroActions}>
                <button
                  onClick={buscarDepositosHoje}
                  style={styles.hojeButton}
                  disabled={loading}
                >
                  <FaCalendarDay /> HOJE
                </button>
                <button
                  onClick={aplicarFiltros}
                  style={styles.filtrarButton}
                  disabled={loading}
                >
                  <FaFilter /> FILTRAR
                </button>
                <button
                  onClick={limparFiltros}
                  style={styles.limparButton}
                  disabled={loading}
                >
                  <FaSync /> LIMPAR
                </button>
                <button
                  onClick={abrirModalNovo}
                  style={styles.addButtonInline}
                  disabled={loading}
                >
                  <FaPlus /> ADICIONAR
                </button>
              </div>
            </div>
          </div>

          {/* Tabela de Depositos */}
          <div style={styles.tableContainer}>
            {loading && depositos.length === 0 ? (
              <div style={styles.loading}>Carregando depositos...</div>
            ) : depositos.length > 0 ? (
              <>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Data</th>
                      <th style={styles.th}>Valor</th>
                      <th style={styles.th}>Descrição</th>
                      <th style={styles.th}>Usuário</th>
                      <th style={styles.th}>Opções</th>
                    </tr>
                  </thead>
                  <tbody>
                    {depositos.map((deposito) => (
                      <tr key={deposito.idDeposito} style={styles.tr}>
                        <td style={styles.tdData}>{formatarData(deposito.dataCadastro)}</td>
                        <td style={styles.tdValor}>
                          <span style={styles.valorDestaque}>
                            {formatarMoeda(deposito.valor)}
                          </span>
                        </td>
                        <td style={styles.tdDescricao}>{deposito.descricao}</td>
                        <td style={styles.tdUsuario}>
                          <div style={styles.usuarioInfo}>
                            <FaUser style={styles.usuarioIcon} />
                            <span>{getNomeUsuario(deposito)}</span>
                          </div>
                        </td>
                        <td style={styles.tdAcoes}>
                          <button
                            onClick={() => abrirModalEditar(deposito)}
                            style={styles.editButton}
                            disabled={loading}
                            title="Editar"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => excluirDeposito(deposito.idDeposito)}
                            style={styles.deleteButton}
                            disabled={loading}
                            title="Excluir"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Paginação */}
                {totalPaginas > 1 && (
                  <div style={styles.pagination}>
                    <button
                      onClick={() => mudarPagina(paginaAtual - 1)}
                      disabled={paginaAtual === 0 || loading}
                      style={styles.paginationButton}
                    >
                      <FaChevronLeft />
                    </button>
                    
                    <span style={styles.paginationInfo}>
                      Página {paginaAtual + 1} de {totalPaginas} 
                      {totalElementos > 0 && ` (${totalElementos} registros)`}
                    </span>
                    
                    <button
                      onClick={() => mudarPagina(paginaAtual + 1)}
                      disabled={paginaAtual === totalPaginas - 1 || loading}
                      style={styles.paginationButton}
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={styles.noResults}>
                <FaMoneyBillWave style={styles.noResultsIcon} />
                <p>Nenhum deposito encontrado</p>
                <small>Clique em "ADICIONAR" para começar</small>
              </div>
            )}
          </div>
        </div>

        {/* Modal de Adicionar/Editar Deposito */}
        {modalAberto && (
          <div style={styles.modalOverlay} onClick={fecharModal}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>
                  <FaMoneyBillWave style={styles.modalTitleIcon} />
                  {modalEdicao ? 'Editar Deposito' : 'Adicionar Deposito'}
                </h2>
                <button onClick={fecharModal} style={styles.modalClose}>
                  <FaTimes />
                </button>
              </div>
              
              <div style={styles.modalContent}>
                <div style={styles.modalForm}>
                  <div style={styles.inputGroup}>
                    <label style={styles.labelRequired}>Valor</label>
                    <div style={styles.valorInputContainer}>
                      <span style={styles.valorSimbolo}>R$</span>
                      <NumericFormat
                        value={formDeposito.valor}
                        onValueChange={(values) => {
                          setFormDeposito({ ...formDeposito, valor: values.floatValue || 0 });
                        }}
                        thousandSeparator="."
                        decimalSeparator=","
                        placeholder="0,00"
                        decimalScale={2}
                        fixedDecimalScale
                        style={styles.valorInput}
                      />
                    </div>
                    {errors.valor && <span style={styles.errorText}>{errors.valor}</span>}
                  </div>
                  
                  <div style={styles.inputGroup}>
                    <label style={styles.labelRequired}>Descrição</label>
                    <textarea
                      value={formDeposito.descricao}
                      onChange={(e) => setFormDeposito({...formDeposito, descricao: e.target.value})}
                      placeholder="Descreva o motivo do deposito..."
                      rows="3"
                      style={styles.textarea}
                    />
                    {errors.descricao && <span style={styles.errorText}>{errors.descricao}</span>}
                  </div>
                </div>
                
                <div style={styles.modalFooter}>
                  <button
                    onClick={fecharModal}
                    style={styles.cancelButton}
                  >
                    <FaTimes /> Cancelar
                  </button>
                  <button
                    onClick={salvarDeposito}
                    style={styles.saveButton}
                    disabled={loading}
                  >
                    <FaSave /> {loading ? 'Salvando...' : (modalEdicao ? 'Atualizar' : 'Salvar')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Estilos atualizados com botões alinhados
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  content: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd',
    marginBottom: '30px'
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  titleIcon: {
    fontSize: '28px',
    color: '#f39c12',
  },
  title: {
    fontSize: '24px',
    margin: 0,
    color: '#333',
    fontWeight: '600'
  },
  formContainer: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 3px 10px rgba(0,0,0,0.1)'
  },
  filtrosContainer: {
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '10px',
    border: '1px solid #e9ecef'
  },
  filtrosWrapper: {
    display: 'flex',
    gap: '20px',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    justifyContent: 'space-between'
  },
  filtroGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minWidth: '180px'
  },
  filtroGroupBusca: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
    minWidth: '250px'
  },
  filtroLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#495057',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  filtroIcon: {
    fontSize: '14px',
    color: '#6c757d'
  },
  filtroInput: {
    padding: '10px 12px',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    fontSize: '14px',
    transition: 'all 0.2s',
    '&:focus': {
      outline: 'none',
      borderColor: '#f39c12',
      boxShadow: '0 0 0 3px rgba(243,156,18,0.1)'
    }
  },
  filtroInputBusca: {
    padding: '10px 12px',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    fontSize: '14px',
    width: '100%',
    '&:focus': {
      outline: 'none',
      borderColor: '#f39c12',
      boxShadow: '0 0 0 3px rgba(243,156,18,0.1)'
    }
  },
  filtroActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  hojeButton: {
    padding: '10px 20px',
    backgroundColor: '#9b59b6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#8e44ad'
    },
    '&:disabled': {
      backgroundColor: '#95a5a6',
      cursor: 'not-allowed'
    }
  },
  filtrarButton: {
    padding: '10px 20px',
    backgroundColor: '#f39c12',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#e67e22'
    },
    '&:disabled': {
      backgroundColor: '#95a5a6',
      cursor: 'not-allowed'
    }
  },
  limparButton: {
    padding: '10px 20px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#5a6268'
    },
    '&:disabled': {
      backgroundColor: '#95a5a6',
      cursor: 'not-allowed'
    }
  },
  addButtonInline: {
    padding: '10px 20px',
    backgroundColor: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.3s',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#229954'
    },
    '&:disabled': {
      backgroundColor: '#95a5a6',
      cursor: 'not-allowed'
    }
  },
  tableContainer: {
    overflowX: 'auto',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white'
  },
  th: {
    backgroundColor: '#f8f9fa',
    padding: '16px 20px',
    textAlign: 'left',
    borderBottom: '2px solid #dee2e6',
    fontWeight: '600',
    color: '#495057',
    fontSize: '15px'
  },
  tr: {
    borderBottom: '1px solid #e9ecef',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#f8f9fa'
    }
  },
  tdData: {
    padding: '16px 20px',
    fontSize: '14px',
    color: '#6c757d',
    fontFamily: 'monospace'
  },
  tdValor: {
    padding: '16px 20px',
    fontWeight: '600'
  },
  valorDestaque: {
    color: '#e67e22',
    fontSize: '16px',
    fontFamily: 'monospace',
    fontWeight: 'bold'
  },
  tdDescricao: {
    padding: '16px 20px',
    fontSize: '15px',
    color: '#495057'
  },
  tdUsuario: {
    padding: '16px 20px',
    fontSize: '14px',
    color: '#6c757d'
  },
  usuarioInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  usuarioIcon: {
    fontSize: '12px',
    color: '#95a5a6'
  },
  tdAcoes: {
    padding: '16px 20px',
    whiteSpace: 'nowrap'
  },
  editButton: {
    padding: '8px 14px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    marginRight: '12px',
    transition: 'background-color 0.2s',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#2980b9'
    },
    '&:disabled': {
      backgroundColor: '#95a5a6',
      cursor: 'not-allowed'
    }
  },
  deleteButton: {
    padding: '8px 14px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#c0392b'
    },
    '&:disabled': {
      backgroundColor: '#95a5a6',
      cursor: 'not-allowed'
    }
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '20px',
    padding: '20px',
    borderTop: '1px solid #e9ecef',
    backgroundColor: '#f8f9fa'
  },
  paginationButton: {
    padding: '10px 16px',
    backgroundColor: '#f39c12',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '14px',
    transition: 'background-color 0.2s',
    '&:hover:not(:disabled)': {
      backgroundColor: '#e67e22'
    },
    '&:disabled': {
      backgroundColor: '#bdc3c7',
      cursor: 'not-allowed'
    }
  },
  paginationInfo: {
    fontSize: '15px',
    color: '#6c757d'
  },
  loading: {
    textAlign: 'center',
    padding: '60px',
    color: '#6c757d',
    fontSize: '16px'
  },
  noResults: {
    textAlign: 'center',
    padding: '60px',
    color: '#6c757d'
  },
  noResultsIcon: {
    fontSize: '48px',
    color: '#bdc3c7',
    marginBottom: '15px'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e9ecef'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    color: '#2c3e50',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  modalTitleIcon: {
    fontSize: '22px',
    color: '#f39c12'
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#6c757d',
    padding: '5px',
    display: 'flex',
    alignItems: 'center',
    transition: 'color 0.2s',
    '&:hover': {
      color: '#e74c3c'
    }
  },
  modalContent: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1
  },
  modalForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  labelRequired: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#2c3e50',
    position: 'relative'
  },
  valorInputContainer: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    overflow: 'hidden',
    transition: 'all 0.2s',
    '&:focus-within': {
      borderColor: '#f39c12',
      boxShadow: '0 0 0 3px rgba(243,156,18,0.1)'
    }
  },
  valorSimbolo: {
    padding: '10px 12px',
    backgroundColor: '#f8f9fa',
    borderRight: '1px solid #dee2e6',
    fontSize: '14px',
    fontWeight: '600',
    color: '#6c757d'
  },
  valorInput: {
    flex: 1,
    padding: '10px 12px',
    border: 'none',
    fontSize: '14px',
    outline: 'none'
  },
  textarea: {
    padding: '10px 12px',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit',
    transition: 'all 0.2s',
    '&:focus': {
      outline: 'none',
      borderColor: '#f39c12',
      boxShadow: '0 0 0 3px rgba(243,156,18,0.1)'
    }
  },
  errorText: {
    color: '#e74c3c',
    fontSize: '12px',
    marginTop: '4px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid #e9ecef'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#5a6268'
    }
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#229954'
    },
    '&:disabled': {
      backgroundColor: '#95a5a6',
      cursor: 'not-allowed'
    }
  }
};

export default GerenciarDepositos;